import { useState } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OfflinePinIcon from '@mui/icons-material/OfflinePin';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { usePatientOfflineStatus } from '../hooks/usePatientOfflineStatus';
import { OFFLINE_STATUS } from '../../services/offlineService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/context/AuthContext';
import RemoveOfflineDialog from './RemoveOfflineDialog.jsx';

/**
 * @param {{ patientId: string, displayName: string, createdTime: number }} props
 */
export default function OfflineActionsCell({ patientId, displayName, createdTime }) {
  const { offlineStatus, downloadPercent, downloadError, startDownload, deleteOfflineData } = usePatientOfflineStatus(patientId, displayName, createdTime);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const { auth } = useAuth();

  const handleViewOffline = async () => {
    const user = auth.currentUser;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    // Skip Firestore write — pass patientId directly via route state so the
    // dashboard can resolve it from IndexedDB without a network call.
    navigate('/dashboard', { replace: true, state: { offlinePatientId: patientId } });
  };

  const handleConfirmRemove = async () => {
    setConfirmOpen(false);
    await deleteOfflineData();
  };

  if (offlineStatus === OFFLINE_STATUS.READY) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: 0.5 }}>
          <Tooltip title="View offline">
            <IconButton onClick={handleViewOffline} size="small">
              <OfflinePinIcon sx={{ color: 'success.main' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove offline data">
            <IconButton onClick={() => setConfirmOpen(true)} size="small" color="error">
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <RemoveOfflineDialog
          open={confirmOpen}
          patientName={displayName}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirmRemove}
        />
      </>
    );
  }

  if (offlineStatus === OFFLINE_STATUS.DOWNLOADING) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 1.5, px: 1 }}>
        <Box sx={{ width: 80, position: 'relative', height: 28 }}>
          <LinearProgress
            variant="determinate"
            value={downloadPercent}
            sx={{
              position: 'absolute',
              top: '50%',
              left: 0,
              transform: 'translateY(-50%)',
              width: '100%',
              borderRadius: 5,
              height: 6,
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              position: 'absolute',
              top: 'calc(50% + 8px)',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: 10,
              whiteSpace: 'nowrap',
            }}
          >
            {downloadPercent}%
          </Typography>
        </Box>
      </Box>
    );
  }

  if (downloadError) {
    return (
      <Tooltip title="Download failed. Click to retry.">
        <IconButton onClick={startDownload} size="small">
          <ErrorOutlineIcon sx={{ color: 'error.main' }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Download for offline use">
      <IconButton onClick={startDownload} size="small">
        <CloudDownloadIcon />
      </IconButton>
    </Tooltip>
  );
}
